import { Component } from "react";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import withNavigate from "../../components/withNavigate";
import { getServiceList, provisionerTypeLogo, checkSpecialChar } from "../../settings/shared/commonMethods";
import CloudService from "../../services/api/cloud";
import ReactSelect from "react-select";
import toast from "react-hot-toast";
import moment from "moment";
import { API_RESPONSE, default_size } from "../../settings/shared/constants";
import SAModalConfirm from "../../components/common/SAModalConfirm";
import SATooltip from "../../components/common/SATooltip";
import SAPagination from "../../components/common/SAPagination";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import { cloud } from "../../settings/shared/CommonMessages";

/* ─────────────────────────────────────────────────────────────
   Static config
───────────────────────────────────────────────────────────── */
const SERVICE_OPTIONS = [
  { value: "STORAGE", label: "Storage" },
  { value: "VIRTUAL_MACHINE", label: "Virtual Machine" },
  { value: "KUBERNETES_CLUSTER", label: "Kubernetes Cluster" },
  { value: "CONTAINER_CLUSTER", label: "Container Cluster" },
];

const CLOUD_OPTIONS = [
  { value: "AWS", label: "AWS" },
  { value: "AZURE", label: "Azure" },
  // { value: "GOOGLE", label: "Google" },
];

/* Error text for the 4 new Azure fields.
   Uses your CommonMessages entry when it exists, otherwise a sensible fallback,
   so a missing message can never leave a field invalid with no explanation. */
const AZURE_MSG = {
  subscriptionId: cloud?.subscriptionIdEmpty || "Please enter the subscription ID",
  tenantId: cloud?.tenantIdEmpty || "Please enter the tenant ID",
  clientId: cloud?.clientIdEmpty || "Please enter the client ID",
  clientSecret: cloud?.clientSecretEmpty || "Please enter the client secret",
};

/* ─────────────────────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────────────────────── */
const T = {
  navy: "#0f172a",
  themePrimary: "var(--activemenu, #3d91e4)",
  accent: "#3b82f6",
  accentDark: "#1d4ed8",
  border: "rgba(0,0,0,0.07)",
  borderMid: "rgba(0,0,0,0.12)",
  bg: "#f8fafc",
  surface: "#ffffff",
  textPrimary: "#0f172a",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  fontSans: "'DM Sans','Segoe UI',sans-serif",
  fontMono: "'DM Mono','Courier New',monospace",
};

/* ─────────────────────────────────────────────────────────────
   SVG Icons
───────────────────────────────────────────────────────────── */
const Ic = {
  Search: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
      <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Plus: () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Cloud: () => (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <path d="M14 13H5a4.5 4.5 0 1 1 .6-8.9A5.5 5.5 0 0 1 15 8.5 3.5 3.5 0 0 1 14 13z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  Close: () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <polyline points="9,2 4,7 9,12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Save: () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <polyline points="1,6 4.5,9.5 11,2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Admin: () => (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="5" r="3" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 13c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  ChevLeft: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <polyline points="6,2 3,5 6,8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevRight: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <polyline points="4,2 7,5 4,8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevDown: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <polyline points="2,3.5 5,6.5 8,3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevUp: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <polyline points="2,6.5 5,3.5 8,6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  PlusSquare: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 5.5v5M5.5 8h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  MinusSquare: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 8h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Refresh: () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 2.5v4h-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 6.5A6 6 0 1 0 11.5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  SortAsc: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M5 2.2L8 6.8H2L5 2.2Z" fill="currentColor" />
    </svg>
  ),
  SortDesc: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M5 7.8L2 3.2h6L5 7.8Z" fill="currentColor" />
    </svg>
  ),
  SortIdle: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M5 1.8L7.2 4.4H2.8L5 1.8Z" fill="currentColor" opacity="0.35" />
      <path d="M5 8.2L2.8 5.6h4.4L5 8.2Z" fill="currentColor" opacity="0.35" />
    </svg>
  ),
  Check: () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <polyline points="2,6.5 5,9.5 10,3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Eye: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  ),
  EyeOff: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 2.5l11 11M6.6 6.6A2 2 0 0 0 9.4 9.4M4.2 4.4C2.7 5.5 1.5 8 1.5 8S4 12.5 8 12.5c1.2 0 2.2-.4 3.1-.9M7 3.6c.3-.1.7-.1 1-.1 4 0 6.5 4.5 6.5 4.5s-.5 1-1.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Info: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 7.2v4M8 4.9v.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
};

const CLOUD_LIST_GRID_COLUMNS =
  "28px minmax(160px, 1.6fr) minmax(90px, 0.8fr) minmax(72px, 0.7fr) minmax(120px, 1.2fr) minmax(100px, 0.9fr) minmax(110px, 1fr) 84px";

function SortableHead({ label, sortKey, activeKey, sortDir, onSort, align = "left" }) {
  const active = activeKey === sortKey;
  return (
    <button
      type="button"
      className="cl-sort-btn"
      onClick={() => onSort(sortKey)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: 0,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        font: "inherit",
        color: active ? T.textPrimary : "inherit",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        width: "100%",
        textTransform: "inherit",
        letterSpacing: "inherit",
        fontWeight: active ? 700 : "inherit",
      }}
    >
      <span>{label}</span>
      <span style={{ display: "inline-flex", color: active ? T.accent : T.textMuted }}>
        {active ? (sortDir === "asc" ? <Ic.SortAsc /> : <Ic.SortDesc />) : <Ic.SortIdle />}
      </span>
    </button>
  );
}

function CloudListSkeleton({ rows = 5 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={`cloud-skeleton-${index}`}
          className="cl-cloud-row cl-list-row--skeleton"
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            padding: "14px 16px",
          }}
        >
          <span className="cl-skel cl-skel-chevron" />
          <span className="cl-skel cl-skel-name" />
          <span className="cl-skel cl-skel-chip" />
          <span className="cl-skel cl-skel-chip" />
          <span className="cl-skel cl-skel-chip" />
          <span className="cl-skel cl-skel-text" />
          <span className="cl-skel cl-skel-text" />
          <span className="cl-skel cl-skel-action" />
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Shared UI primitives
───────────────────────────────────────────────────────────── */
function Field({ label, required, error, hint, children }) {
  return (
    <div style={{ marginBottom: 0 }}>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>
          {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 5 }}>{hint}</div>}
      {error && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 5 }}>{error}</div>}
    </div>
  );
}

function Input({ hasError, style, ...rest }) {
  return (
    <input
      {...rest}
      style={{
        width: "100%", height: 38, padding: "0 12px",
        border: `1px solid ${hasError ? "#dc2626" : "#d5dde7"}`,
        borderRadius: 9, background: "#fff",
        fontFamily: T.fontSans, fontSize: 13, color: T.textPrimary,
        outline: "none", boxSizing: "border-box",
        ...style,
      }}
    />
  );
}

function Textarea({ style, ...rest }) {
  return (
    <textarea
      {...rest}
      style={{
        width: "100%", padding: "8px 12px", minHeight: 38,
        border: "1px solid #d5dde7", borderRadius: 9, background: "#fff",
        fontFamily: T.fontSans, fontSize: 13, color: T.textPrimary,
        outline: "none", resize: "vertical", boxSizing: "border-box",
        ...style,
      }}
    />
  );
}

function BtnPrimary({ children, style, ...rest }) {
  return (
    <button {...rest} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.themePrimary, color: "#fff", border: "none", borderRadius: 8, padding: "0 16px", height: 36, fontFamily: T.fontSans, fontSize: 13, fontWeight: 500, cursor: "pointer", ...style }}>
      {children}
    </button>
  );
}

function BtnSecondary({ children, style, ...rest }) {
  return (
    <button {...rest} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: T.textPrimary, border: `0.5px solid ${T.borderMid}`, borderRadius: 8, padding: "0 14px", height: 36, fontFamily: T.fontSans, fontSize: 13, cursor: "pointer", ...style }}>
      {children}
    </button>
  );
}

/* react-select custom styles to match design system */
const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    border: `1px solid ${hasError ? "#dc2626" : state.isFocused ? T.accent : "#d5dde7"}`,
    borderRadius: 9,
    background: "#fff",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.16)" : "none",
    fontFamily: T.fontSans,
    fontSize: 13,
    "&:hover": { borderColor: hasError ? "#dc2626" : state.isFocused ? T.accent : "#b6c3d4" },
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: `0.5px solid ${T.borderMid}`, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", fontSize: 13, fontFamily: T.fontSans, zIndex: 20 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({ ...base, background: state.isSelected ? T.themePrimary : state.isFocused ? "#f1f5f9" : "transparent", color: state.isSelected ? "#fff" : T.textPrimary, fontSize: 13 }),
  singleValue: (base) => ({ ...base, color: T.textPrimary, fontSize: 13 }),
  placeholder: (base) => ({ ...base, color: T.textMuted, fontSize: 13 }),
  indicatorSeparator: () => ({ display: "none" }),
});

/* Service badge */
function ServiceBadge({ label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 100, background: "#e0f2fe", color: "#0369a1", fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function DetailField({ label, value, mono = false }) {
  const empty = value === undefined || value === null || value === "";
  return (
    <div style={{ background: "#fff", border: `0.5px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", minHeight: 64 }}>
      <div style={{ fontSize: 10, fontWeight: 650, textTransform: "uppercase", letterSpacing: "0.05em", color: T.textMuted, marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: empty ? T.textMuted : T.textPrimary,
          fontFamily: mono ? T.fontMono : T.fontSans,
          wordBreak: "break-word",
          lineHeight: 1.4,
        }}
        title={empty ? undefined : String(value)}
      >
        {empty ? "—" : String(value)}
      </div>
    </div>
  );
}

function getCloudStatusMeta(enabled) {
  if (enabled) {
    return { label: "Enabled", bg: "#ecfdf5", color: "#059669", border: "#a7f3d0", dot: "#10b981" };
  }
  return { label: "Disabled", bg: "#f8fafc", color: "#64748b", border: "rgba(0,0,0,0.06)", dot: "#94a3b8" };
}

function CloudAvatar({ name, provisionerType }) {
  const initial = String(name || "C").trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: 11,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(145deg, #eff6ff 0%, #f8fafc 100%)",
        color: T.accent,
        border: `1px solid ${T.border}`,
        position: "relative",
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.02em" }}>{initial}</span>
      <span
        style={{
          position: "absolute",
          right: -3,
          bottom: -3,
          width: 18,
          height: 18,
          borderRadius: 6,
          background: T.surface,
          border: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img src={provisionerTypeLogo(provisionerType)} alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />
      </span>
    </div>
  );
}

function CloudExpandPanel({ item }) {
  const enabled = item?.provisionerStatus === "ENABLED";
  const statusMeta = getCloudStatusMeta(enabled);
  const services = Array.isArray(item?.services) ? item.services : [];

  return (
    <div className="cl-expand-panel" onClick={(e) => e.stopPropagation()}>
      <div className="cl-expand-meta">
        <div className="cl-expand-meta-item">
          <div className="cl-expand-meta-label">Status</div>
          <div className="cl-expand-meta-value">
            <span className="cl-meta-chip" style={{ background: statusMeta.bg, color: statusMeta.color, borderColor: statusMeta.border }}>
              {statusMeta.label}
            </span>
          </div>
        </div>
        <div className="cl-expand-meta-item">
          <div className="cl-expand-meta-label">Cloud type</div>
          <div className="cl-expand-meta-value">{item?.provisionerType || "—"}</div>
        </div>
        <div className="cl-expand-meta-item">
          <div className="cl-expand-meta-label">Created</div>
          <div className="cl-expand-meta-value">
            {item?.createdDate
              ? moment.utc(item.createdDate).local().format("MMM DD, YYYY · hh:mm A")
              : "—"}
          </div>
        </div>
        <div className="cl-expand-meta-item">
          <div className="cl-expand-meta-label">Last modified</div>
          <div className="cl-expand-meta-value">
            {item?.updatedDate
              ? moment.utc(item.updatedDate).local().format("MMM DD, YYYY · hh:mm A")
              : "—"}
          </div>
        </div>
      </div>

      {item?.description?.trim() && (
        <div className="cl-expand-section">
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>Description</div>
          <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5 }}>{item.description.trim()}</div>
        </div>
      )}

      <div className="cl-expand-section">
        <div style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: services.length ? 10 : 0 }}>
          Services{services.length ? ` · ${services.length}` : ""}
        </div>
        {services.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {services?.map((s, i) => <ServiceBadge key={i} label={s} />)}
          </div>
        ) : (
          <span style={{ fontSize: 11, color: T.textMuted }}>None configured</span>
        )}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div className="cl-expand-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
          <DetailField label="Created by" value={item?.createdBy} />
          <DetailField label="Name" value={item?.name} />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
class CloudList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cloudData: [],
      totalPages: 0,
      loading: false,
      currentPage: 0,
      pageSize: 10,
      deleteFlag: false,
      modelItem: {},
      searchValue: "",
      // Create cloud form
      showCloudAction: false,
      submitFlag: false,
      name: "",
      description: "",
      services: [],
      provisionerType: "AWS",
      accessKey: "",
      secretKey: "",
      enableCloudAccess: true,
      platformDefault: true,
      showSecret: false,
      // Azure · storage
      accountName: "",
      accountAccessKey: "",
      // Azure · every other service (service principal)
      subscriptionId: "",
      tenantId: "",
      clientId: "",
      clientSecret: "",
      // List
      expandedId: null,
      sortKey: null,
      sortDir: null,
    };

    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this.getCloudList();
    window.addEventListener("resize", this.updateWindowDimensions);
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener("resize", this.updateWindowDimensions);
  }

  updateWindowDimensions = () => this.setState({ height: window.innerHeight });

  /* ── API ── */
  getCloudList = () => {
    this.setState({ loading: true });
    CloudService.getcloudList(0, default_size)
      .then((response) => {
        if (!this._isMounted) return;
        if (response.status === "SUCCESS") {
          const page = response.provisionerDTOPage || {};
          this.setState({
            cloudData: page.content || [],
            loading: false,
          });
        } else {
          this.setState({ loading: false });
          toast.error(response.errorMessage);
        }
      })
      .catch(() => {
        if (this._isMounted) this.setState({ loading: false });
      });
  };

  handlePaginationClick = (pageNumber) => {
    if (pageNumber === this.state.currentPage) return;
    this.setState({ currentPage: pageNumber, expandedId: null });
  };

  handleSearchChange = (e) => this.setState({ searchValue: e.target.value, currentPage: 0 });

  handleSort = (key) => {
    this.setState((prev) => ({
      sortKey: key,
      sortDir: prev.sortKey === key && prev.sortDir === "asc" ? "desc" : "asc",
      currentPage: 0,
    }));
  };

  toggleRow = (cloudId) => {
    this.setState((prev) => ({
      expandedId: prev.expandedId === cloudId ? null : cloudId,
    }));
  };

  refreshCloudList = () => {
    this.setState({ expandedId: null }, () => this.getCloudList());
  };

  /* ── Delete ── */
  removeCloudConfirmation = (e, item) => {
    e.preventDefault();
    this.setState({ deleteFlag: true, modelItem: item });
  };

  handleSubmitModal = () => {
    CloudService.deletecloud(this.state.modelItem?.id)
      .then((response) => {
        if (!this._isMounted) return;
        if (response.status === API_RESPONSE.SUCCESS) {
          this.setState({ deleteFlag: false, modelItem: {} });
          this.getCloudList();
          toast.success("Cloud deleted successfully");
        } else {
          toast.error(response?.errorMessage);
        }
      })
      .catch((error) => toast.error(error));
  };

  /* ── Form ── */
  showCloudActionForm = () => this.setState({ showCloudAction: true });

  hideCloudActionForm = () => {
    this.setState({
      showCloudAction: false, submitFlag: false,
      name: "", description: "", services: [], provisionerType: "AWS",
      accessKey: "", secretKey: "", enableCloudAccess: true,
      accountName: "", accountAccessKey: "",
      subscriptionId: "", tenantId: "", clientId: "", clientSecret: "",
      platformDefault: true, showSecret: false,
    });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleChangeService = (selected) => {
    this.setState({ services: selected ? [selected] : [] });
  };

  handleEnableCloud = (e) => this.setState({ enableCloudAccess: e.target.checked });

  handleChangeCloudTypes = (type) => this.setState({ provisionerType: type });

  /* The service is stored as { value, label }, so compare .value (not the object) */
  isStorageService = () => this.state.services?.[0]?.value === "STORAGE";

  provisionerTypesConfigs = () => {
    const { provisionerType } = this.state;

    if (provisionerType === "AWS") {
      return { accessKeyId: this.state.accessKey, secretAccessKey: this.state.secretKey };
    }

    if (provisionerType === "AZURE") {
      // Storage uses the storage-account keys
      if (this.isStorageService()) {
        return { accountName: this.state.accountName, accountAccessKey: this.state.accountAccessKey };
      }
      // Every other service uses an Azure service principal.
      // ⚠ Adjust these 4 key names if your backend expects different ones.
      return {
        subscriptionId: this.state.subscriptionId,
        tenantId: this.state.tenantId,
        clientId: this.state.clientId,
        secretKey: this.state.clientSecret,
      };
    }

    return {};
  };

  updateProvisioner = () => {
    const selectedServices = Array.isArray(this.state.services) ? this.state.services : [];
    const service = selectedServices.map((item) => item.value);
    const post = {
      accountId: localStorage.getItem("accountId"),
      platformDefault: this.state.platformDefault,
      name: this.state.name,
      description: this.state.description,
      provisionerType: this.state.provisionerType,
      provisionerStatus: this.state.enableCloudAccess ? "ENABLED" : "DISABLED",
      provisionerConfig: this.provisionerTypesConfigs(),
      services: service,
    };
    CloudService.createcloud(post).then((response) => {
      if (!this._isMounted) return;
      if (response.status === "SUCCESS") {
        showNotificationGlobal("Cloud created successfully!");
        this.getCloudList();
        this.hideCloudActionForm();
      } else {
        showNotificationGlobal(response.errorMessage);
      }
    });
  };

  CreateCloud = () => {
    this.setState({ submitFlag: true }, () => {
      const invalid = document.getElementsByClassName("is-invalid");
      if (invalid?.length === 0) this.updateProvisioner();
    });
  };

  /* ── Search / sort / page ── */
  getCloudSortValue = (item, key) => {
    switch (key) {
      case "name":
        return (item?.name || "").toLowerCase();
      case "status":
        return (item?.provisionerStatus || "").toLowerCase();
      case "type":
        return (item?.provisionerType || "").toLowerCase();
      case "services":
        return Array.isArray(item?.services) ? item.services.length : 0;
      case "createdBy":
        return (item?.createdBy || "").toLowerCase();
      case "modified":
        return item?.updatedDate ? new Date(item.updatedDate).getTime() : 0;
      default:
        return "";
    }
  };

  getFilteredClouds = () => {
    const { cloudData, searchValue, sortKey, sortDir } = this.state;
    let list = cloudData;

    if (searchValue.trim()) {
      const q = searchValue.toLowerCase().trim();
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q) ||
          (c.provisionerType || "").toLowerCase().includes(q) ||
          (c.createdBy || "").toLowerCase().includes(q)
      );
    }

    if (!sortKey || !sortDir) return list;

    const direction = sortDir === "desc" ? -1 : 1;
    return [...list].sort((a, b) => {
      const av = this.getCloudSortValue(a, sortKey);
      const bv = this.getCloudSortValue(b, sortKey);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * direction;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * direction;
    });
  };

  getPagedClouds = () => {
    const filtered = this.getFilteredClouds();
    const pageSize = this.state.pageSize || 10;
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const currentPage = Math.min(this.state.currentPage, totalPages - 1);
    const start = currentPage * pageSize;
    return {
      items: filtered.slice(start, start + pageSize),
      totalPages,
      currentPage,
      totalItems: filtered.length,
    };
  };

  /* ─────────────────────────────────────────────────────────
     RENDER: Create Cloud Form
  ───────────────────────────────────────────────────────── */
  renderCreateCloud() {
    const {
      submitFlag, provisionerType, enableCloudAccess, services,
      showSecret, name, description, accessKey, secretKey,
      accountName, accountAccessKey,
      subscriptionId, tenantId, clientId, clientSecret,
    } = this.state;

    const selectedServices = Array.isArray(services) ? services : [];
    const isAWS = provisionerType === "AWS";
    const isAzure = provisionerType === "AZURE";

    const serviceChosen = selectedServices.length > 0;
    const isStorage = selectedServices[0]?.value === "STORAGE";
    const showAzureStorage = isAzure && serviceChosen && isStorage;
    const showAzurePrincipal = isAzure && serviceChosen && !isStorage;

    const nameInvalid = submitFlag && (!name || !checkSpecialChar(name));
    const serviceInvalid = submitFlag && !serviceChosen;
    const providerLabel = CLOUD_OPTIONS.find((o) => o.value === provisionerType)?.label || provisionerType;
    const statusMeta = getCloudStatusMeta(enableCloudAccess);

    const credsDone = isAWS
      ? !!(accessKey && secretKey)
      : showAzureStorage
        ? !!(accountName && accountAccessKey)
        : showAzurePrincipal
          ? !!(subscriptionId && tenantId && clientId && clientSecret)
          : false;

    // Same order as the form: details → service → credentials
    const checklist = [
      { label: "Name the cloud", done: !!name.trim() },
      { label: "Choose a service", done: serviceChosen },
      { label: "Add credentials", done: credsDone },
    ];
    const ready = checklist.every((c) => c.done);

    const credsDesc = isAWS
      ? "AWS access credentials"
      : showAzureStorage
        ? "Azure storage account credentials"
        : showAzurePrincipal
          ? "Azure service principal credentials"
          : "Cloud provider credentials";

    const secretToggle = (
      <button
        type="button"
        className="cc-eye"
        aria-label={showSecret ? "Hide secret" : "Show secret"}
        title={showSecret ? "Hide" : "Show"}
        onClick={() => this.setState({ showSecret: !showSecret })}
      >
        {showSecret ? <Ic.EyeOff /> : <Ic.Eye />}
      </button>
    );

    return (
      <div className="cc-page">
        <style>{`
          .cc-page { font-family: ${T.fontSans}; background: ${T.bg}; min-height: 100vh; color: ${T.textPrimary}; }
          .cc-head { position: sticky; top: 0; z-index: 10; background: ${T.surface}; border-bottom: 1px solid ${T.border};
                     padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
          .cc-head-main { display: flex; align-items: center; gap: 12px; min-width: 0; }
          .cc-head-icon { width: 36px; height: 36px; border-radius: 10px; background: ${T.themePrimary}; color: #fff;
                          display: grid; place-items: center; flex-shrink: 0; }
          .cc-title { font-size: 15px; font-weight: 650; line-height: 1.25; }
          .cc-sub { font-size: 12px; color: ${T.textSecondary}; margin-top: 1px; }

          .cc-wrap { max-width: 1080px; margin: 0 auto; padding: 24px 24px 0;
                     display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 20px; align-items: start; }
          .cc-card { background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 14px; margin-bottom: 16px; }
          .cc-card-head { padding: 16px 20px; border-bottom: 1px solid ${T.border}; }
          .cc-card-title { font-size: 14px; font-weight: 650; }
          .cc-card-desc { font-size: 12px; color: ${T.textSecondary}; margin-top: 2px; }
          .cc-card-body { padding: 20px; display: flex; flex-direction: column; gap: 18px; }
          .cc-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 20px; }

          .cc-form input:focus, .cc-form textarea:focus { border-color: ${T.accent} !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.16); }

          /* provider picker */
          .cc-providers { display: flex; gap: 10px; flex-wrap: wrap; }
          .cc-provider { position: relative; display: flex; align-items: center; gap: 10px; min-width: 150px;
                         padding: 10px 14px; border-radius: 11px; background: ${T.surface}; border: 1px solid #d5dde7;
                         font-family: ${T.fontSans}; font-size: 13px; font-weight: 550; color: ${T.textPrimary};
                         cursor: pointer; transition: border-color .15s, background .15s, box-shadow .15s; }
          .cc-provider:hover { border-color: #b6c3d4; }
          .cc-provider.is-on { border-color: ${T.accent}; background: #f3f8ff; box-shadow: 0 0 0 3px rgba(59,130,246,0.14); }
          .cc-provider img { width: 26px; height: 26px; object-fit: contain; }
          .cc-provider-check { margin-left: auto; width: 18px; height: 18px; border-radius: 50%; display: grid; place-items: center;
                               background: ${T.accent}; color: #fff; opacity: 0; transform: scale(.7); transition: opacity .15s, transform .15s; }
          .cc-provider.is-on .cc-provider-check { opacity: 1; transform: none; }

          /* secret reveal */
          .cc-secret { position: relative; }
          .cc-eye { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); width: 28px; height: 28px; border: 0;
                    background: transparent; color: ${T.textSecondary}; border-radius: 7px; display: grid; place-items: center; cursor: pointer; }
          .cc-eye:hover { background: #f1f5f9; color: ${T.textPrimary}; }

          /* hint shown before a service is picked (Azure) */
          .cc-note { display: flex; gap: 9px; align-items: flex-start; padding: 12px 14px; border-radius: 11px;
                     background: #f3f8ff; border: 1px solid #d6e6fb; color: #1e4f9a; font-size: 12.5px; line-height: 1.45; }
          .cc-note svg { flex-shrink: 0; margin-top: 1px; }

          /* access rows */
          .cc-row-toggle { display: flex; align-items: center; justify-content: space-between; gap: 14px;
                           padding: 12px 14px; border: 1px solid #d5dde7; border-radius: 11px; }
          .cc-row-toggle.is-on { background: #f3f8ff; border-color: #bfd7fb; }
          .cc-row-title { font-size: 13px; font-weight: 600; }
          .cc-row-hint { font-size: 11.5px; color: ${T.textSecondary}; margin-top: 2px; }
          .cc-switch { width: 40px; height: 22px; border-radius: 100px; border: 0; padding: 0; cursor: pointer; position: relative;
                       background: #cbd5e1; transition: background .2s; flex-shrink: 0; }
          .cc-switch.is-on { background: ${T.accent}; }
          .cc-switch i { position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff;
                         box-shadow: 0 1px 2px rgba(15,23,42,.18); transition: left .2s; }
          .cc-switch.is-on i { left: 21px; }

          /* summary (right) */
          .cc-side { position: sticky; top: 84px; }
          .cc-summary { padding: 18px; }
          .cc-summary-top { display: flex; gap: 12px; align-items: center; }
          .cc-summary-name { font-size: 14px; font-weight: 650; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .cc-summary-name.is-empty { color: ${T.textMuted}; font-weight: 500; }
          .cc-summary-desc { font-size: 12px; color: ${T.textSecondary}; margin-top: 12px; line-height: 1.5; word-break: break-word; }
          .cc-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 14px; }
          .cc-divider { height: 1px; background: ${T.border}; margin: 16px 0 14px; }
          .cc-check { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: ${T.textSecondary}; padding: 4px 0; }
          .cc-check-dot { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid #cbd5e1; display: grid; place-items: center; color: #fff; flex-shrink: 0; transition: all .15s; }
          .cc-check.is-done { color: ${T.textPrimary}; }
          .cc-check.is-done .cc-check-dot { background: #10b981; border-color: #10b981; }

          /* sticky action bar */
          .cc-actions { position: sticky; bottom: 0; z-index: 5; margin: 0 -24px; padding: 14px 24px;
                        background: rgba(248,250,252,0.92); backdrop-filter: blur(6px); border-top: 1px solid ${T.border};
                        display: flex; align-items: center; justify-content: space-between; gap: 12px; }
          .cc-actions-note { font-size: 12px; color: ${T.textSecondary}; }
          .cc-actions-note.is-ready { color: #059669; font-weight: 550; }

          /* chip used by the summary (the list's .cl-meta-chip lives in the list <style>) */
          .cc-page .cl-meta-chip { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 8px; font-size: 11px;
                                   font-weight: 500; white-space: nowrap; background: #f1f5f9; color: #64748b; border: 1px solid rgba(0,0,0,0.05); }

          @media (max-width: 900px) {
            .cc-wrap { grid-template-columns: 1fr; padding: 16px 16px 0; }
            .cc-side { position: static; order: -1; }
            .cc-grid2 { grid-template-columns: 1fr; }
            .cc-head { padding: 0 16px; }
            .cc-actions { margin: 0 -16px; padding: 12px 16px; }
          }
          @media (prefers-reduced-motion: reduce) { .cc-page * { transition: none !important; } }
        `}</style>

        {/* Header */}
        <div className="cc-head">
          <div className="cc-head-main">
            <div className="cc-head-icon"><Ic.Cloud /></div>
            <div style={{ minWidth: 0 }}>
              <div className="cc-title">Add cloud</div>
              <div className="cc-sub">Connect a cloud provider and choose how it's used</div>
            </div>
          </div>
          <BtnSecondary onClick={this.hideCloudActionForm}>
            <Ic.ArrowLeft /> Back to clouds
          </BtnSecondary>
        </div>

        <div className="cc-wrap cc-form">
          {/* ───────────── Left: form ───────────── */}
          <div>
            {/* 1 · Details */}
            <section className="cc-card">
              <div className="cc-card-head">
                <div className="cc-card-title">Details</div>
                <div className="cc-card-desc">How this cloud is named across the platform.</div>
              </div>
              <div className="cc-card-body">
                <div className="cc-grid2">
                  <Field label="Name" required error={nameInvalid ? cloud.appNameSpecial : ""}>
                    <Input
                      type="text"
                      name="name"
                      placeholder="e.g. prod-aws"
                      value={name}
                      onChange={this.handleChange}
                      autoComplete="off"
                      autoFocus
                      hasError={nameInvalid}
                      className={nameInvalid ? "is-invalid" : ""}
                    />
                  </Field>
                  <Field label="Description" hint="Optional">
                    <Textarea name="description" rows={1} placeholder="What is this cloud used for?" value={description} onChange={this.handleChange} />
                  </Field>
                </div>
              </div>
            </section>

            {/* 2 · Access & scope (service first — Azure credentials depend on it) */}
            <section className="cc-card">
              <div className="cc-card-head">
                <div className="cc-card-title">Access &amp; scope</div>
                <div className="cc-card-desc">What this cloud can be used for.</div>
              </div>
              <div className="cc-card-body">
                <Field label="Service" required error={serviceInvalid ? cloud.serviceErrror : ""}>
                  <ReactSelect
                    isClearable
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    placeholder="Select a service…"
                    value={selectedServices[0] || null}
                    onChange={this.handleChangeService}
                    options={SERVICE_OPTIONS}
                    styles={selectStyles(serviceInvalid)}
                    className={serviceInvalid ? "is-invalid" : ""}
                  />
                </Field>

                <div className={`cc-row-toggle${enableCloudAccess ? " is-on" : ""}`}>
                  <div>
                    <div className="cc-row-title">Enable cloud access</div>
                    <div className="cc-row-hint">
                      {enableCloudAccess ? "Active — the cloud can be used right away." : "Inactive — saved, but not usable until enabled."}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enableCloudAccess}
                    aria-label="Enable cloud access"
                    className={`cc-switch${enableCloudAccess ? " is-on" : ""}`}
                    onClick={() => this.setState({ enableCloudAccess: !enableCloudAccess })}
                  >
                    <i />
                  </button>
                </div>
              </div>
            </section>

            {/* 3 · Provider & credentials */}
            <section className="cc-card">
              <div className="cc-card-head">
                <div className="cc-card-title">Provider &amp; credentials</div>
                <div className="cc-card-desc">{credsDesc}</div>
              </div>
              <div className="cc-card-body">
                <Field label="Cloud type" required>
                  <div className="cc-providers" role="radiogroup" aria-label="Cloud type">
                    {CLOUD_OPTIONS.map((p) => {
                      const on = provisionerType === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          className={`cc-provider${on ? " is-on" : ""}`}
                          onClick={() => this.handleChangeCloudTypes(p.value)}
                        >
                          <img src={provisionerTypeLogo(p.value)} alt="" />
                          {p.label}
                          <span className="cc-provider-check"><Ic.Check /></span>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {/* AWS */}
                {isAWS && (
                  <div className="cc-grid2">
                    <Field label="Access key" required error={submitFlag && !accessKey ? cloud.accessKeyEmpty : ""}>
                      <Input type="text" name="accessKey" placeholder="AKIAIOSFODNN7EXAMPLE" value={accessKey} onChange={this.handleChange} autoComplete="off" hasError={submitFlag && !accessKey} className={submitFlag && !accessKey ? "is-invalid" : ""} />
                    </Field>
                    <Field label="Secret key" required error={submitFlag && !secretKey ? cloud.secretKeyEmpty : ""}>
                      <div className="cc-secret">
                        <Input type={showSecret ? "text" : "password"} name="secretKey" placeholder="••••••••••••••••••••" value={secretKey} onChange={this.handleChange} autoComplete="off" style={{ paddingRight: 40 }} hasError={submitFlag && !secretKey} className={submitFlag && !secretKey ? "is-invalid" : ""} />
                        {secretToggle}
                      </div>
                    </Field>
                  </div>
                )}

                {/* Azure · no service picked yet */}
                {isAzure && !serviceChosen && (
                  <div className="cc-note">
                    <Ic.Info />
                    <span>Choose a service above to see which Azure credentials are needed.</span>
                  </div>
                )}

                {/* Azure · Storage → storage account */}
                {showAzureStorage && (
                  <div className="cc-grid2">
                    <Field label="Account name" required error={submitFlag && !accountName ? cloud.accountNameEmpty : ""}>
                      <Input type="text" name="accountName" placeholder="Account name" value={accountName} onChange={this.handleChange} autoComplete="off" hasError={submitFlag && !accountName} className={submitFlag && !accountName ? "is-invalid" : ""} />
                    </Field>
                    <Field label="Account access key" required error={submitFlag && !accountAccessKey ? cloud.accountAccessKeyEmpty : ""}>
                      <div className="cc-secret">
                        <Input type={showSecret ? "text" : "password"} name="accountAccessKey" placeholder="Account access key" value={accountAccessKey} onChange={this.handleChange} autoComplete="off" style={{ paddingRight: 40 }} hasError={submitFlag && !accountAccessKey} className={submitFlag && !accountAccessKey ? "is-invalid" : ""} />
                        {secretToggle}
                      </div>
                    </Field>
                  </div>
                )}

                {/* Azure · any other service → service principal (4 values) */}
                {showAzurePrincipal && (
                  <div className="cc-grid2">
                    <Field label="Subscription ID" required error={submitFlag && !subscriptionId ? AZURE_MSG.subscriptionId : ""}>
                      <Input
                        type="text"
                        name="subscriptionId"
                        placeholder="Subscription Id"
                        value={subscriptionId}
                        onChange={this.handleChange}
                        autoComplete="off"
                        hasError={submitFlag && !subscriptionId}
                        className={submitFlag && !subscriptionId ? "is-invalid" : ""}
                      />
                    </Field>
                    <Field label="Tenant ID" required error={submitFlag && !tenantId ? AZURE_MSG.tenantId : ""}>
                      <Input
                        type="text"
                        name="tenantId"
                        placeholder="Tenant Id"
                        value={tenantId}
                        onChange={this.handleChange}
                        autoComplete="off"
                        hasError={submitFlag && !tenantId}
                        className={submitFlag && !tenantId ? "is-invalid" : ""}
                      />
                    </Field>
                    <Field label="Client ID" required error={submitFlag && !clientId ? AZURE_MSG.clientId : ""}>
                      <Input
                        type="text"
                        name="clientId"
                        placeholder="Client Id"
                        value={clientId}
                        onChange={this.handleChange}
                        autoComplete="off"
                        hasError={submitFlag && !clientId}
                        className={submitFlag && !clientId ? "is-invalid" : ""}
                      />
                    </Field>
                    <Field label="Client secret" required error={submitFlag && !clientSecret ? AZURE_MSG.clientSecret : ""}>
                      <div className="cc-secret">
                        <Input
                          type={showSecret ? "text" : "password"}
                          name="clientSecret"
                          placeholder="Client secret"
                          value={clientSecret}
                          onChange={this.handleChange}
                          autoComplete="off"
                          style={{ paddingRight: 40 }}
                          hasError={submitFlag && !clientSecret}
                          className={submitFlag && !clientSecret ? "is-invalid" : ""}
                        />
                        {secretToggle}
                      </div>
                    </Field>
                  </div>
                )}
              </div>
            </section>

            {/* Sticky action bar */}
            <div className="cc-actions">
              <span className={`cc-actions-note${ready ? " is-ready" : ""}`}>
                {ready ? "Ready to create" : `${checklist.filter((c) => c.done).length} of ${checklist.length} steps complete`}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <BtnSecondary onClick={this.hideCloudActionForm}>
                  <Ic.Close /> Cancel
                </BtnSecondary>
                <BtnPrimary onClick={this.CreateCloud} style={{ background: T.accentDark }}>
                  <Ic.Save /> Create cloud
                </BtnPrimary>
              </div>
            </div>
          </div>

          {/* ───────────── Right: live summary ───────────── */}
          <aside className="cc-side" aria-label="Summary">
            <div className="cc-card cc-summary">
              <div className="cc-summary-top">
                <CloudAvatar name={name || "C"} provisionerType={provisionerType} />
                <div style={{ minWidth: 0 }}>
                  <div className={`cc-summary-name${name.trim() ? "" : " is-empty"}`}>{name.trim() || "Untitled cloud"}</div>
                  <div className="cc-sub">{providerLabel}</div>
                </div>
              </div>

              {description.trim() && <div className="cc-summary-desc">{description.trim()}</div>}

              <div className="cc-chips">
                <span className="cl-meta-chip" style={{ background: statusMeta.bg, color: statusMeta.color, borderColor: statusMeta.border }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusMeta.dot, marginRight: 6 }} />
                  {statusMeta.label}
                </span>
                {selectedServices[0] && <ServiceBadge label={selectedServices[0].label} />}
              </div>

              <div className="cc-divider" />

              {checklist.map((c) => (
                <div key={c.label} className={`cc-check${c.done ? " is-done" : ""}`}>
                  <span className="cc-check-dot">{c.done && <Ic.Check />}</span>
                  {c.label}
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────
     RENDER: Cloud List
  ───────────────────────────────────────────────────────── */
  render() {
    if (this.state.showCloudAction) return this.renderCreateCloud();

    const { items: filtered, totalPages, currentPage, totalItems } = this.getPagedClouds();
    const hasFilters = !!this.state.searchValue;

    return (
      <>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          .cl-cloud-card {
            background: ${T.surface};
            border: 1px solid ${T.border};
            border-radius: 12px;
            overflow: hidden;
            transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
          }
          .cl-cloud-card:hover {
            border-color: rgba(59, 130, 246, 0.28);
            box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
            transform: translateY(-1px);
          }
          .cl-cloud-card.expanded {
            background: #fafbfc;
            border-color: rgba(59, 130, 246, 0.22);
          }
          .cl-cloud-row {
            display: grid;
            grid-template-columns: ${CLOUD_LIST_GRID_COLUMNS};
            gap: 0 12px;
            align-items: center;
            padding: 14px 16px;
            cursor: pointer;
            transition: background 0.12s ease;
          }
          .cl-cloud-head {
            display: grid;
            grid-template-columns: ${CLOUD_LIST_GRID_COLUMNS};
            gap: 0 12px;
            align-items: center;
            padding: 0 16px 6px;
            font-size: 11px;
            font-weight: 600;
            color: ${T.textMuted};
            text-transform: uppercase;
            letter-spacing: 0.04em;
          }
          .cl-cloud-head span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .cl-sort-btn:hover { color: ${T.textPrimary} !important; }
          .cl-pagination .page-footer {
            margin-top: 0 !important;
            padding: 4px 0 !important;
            border-radius: 0;
            min-height: 0;
          }
          .cl-pagination .pagination-info {
            font-size: 11px !important;
            line-height: 1.2;
          }
          .cl-pagination .go-to-page {
            margin-top: 4px !important;
          }
          .cl-pagination .go-to-page label {
            font-size: 11px;
            color: ${T.textSecondary};
            white-space: nowrap;
          }
          .cl-pagination .go-to-page .form-control {
            height: 26px;
            font-size: 12px;
            padding: 2px 6px;
          }
          .cl-pagination .go-to-page .btn {
            height: 26px;
            padding: 0 8px;
            font-size: 11px;
            line-height: 1;
            background: #3b82f6 !important;
            border-color: #3b82f6 !important;
            color: #fff !important;
          }
          .cl-pagination .go-to-page .btn:hover,
          .cl-pagination .go-to-page .btn:focus {
            background: #2563eb !important;
            border-color: #2563eb !important;
            color: #fff !important;
          }
          .cl-pagination .pagination {
            gap: 2px;
          }
          .cl-pagination .pagination .page-link {
            padding: 2px 8px !important;
            margin: 0 !important;
            font-size: 12px;
            line-height: 1.2;
            min-width: 28px;
            height: 28px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px !important;
          }
          .cl-cloud-expand-btn {
            width: 28px;
            height: 28px;
            padding: 0;
            border: none;
            background: transparent;
            color: ${T.textSecondary};
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 7px;
          }
          .cl-cloud-expand-btn:hover {
            background: #f1f5f9;
            color: ${T.textPrimary};
          }
          .cl-col-actions {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            min-width: 0;
            gap: 8px;
          }
          .cl-act {
            flex-shrink: 0;
          }
          .cl-act:hover { background: #f1f5f9; border-radius: 8px; }
          .cl-act.del:hover { background: #fee2e2; }
          .cl-act.del:hover svg { color: #dc2626; }
          .cl-meta-chip {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 8px;
            font-size: 11px;
            font-weight: 500;
            white-space: nowrap;
            background: #f1f5f9;
            color: #64748b;
            border: 1px solid rgba(0,0,0,0.05);
          }
          .cl-expand-panel {
            padding: 14px 16px 16px;
            background: #f8fafc;
            border-top: 1px solid rgba(0,0,0,0.06);
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .cl-expand-meta {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
          }
          .cl-expand-meta-label {
            font-size: 11px;
            font-weight: 600;
            color: ${T.textMuted};
            margin-bottom: 4px;
          }
          .cl-expand-meta-value {
            font-size: 12px;
            color: ${T.textPrimary};
            font-weight: 500;
          }
          .cl-expand-section {
            background: ${T.surface};
            border: 1px solid ${T.border};
            border-radius: 10px;
            padding: 12px 14px;
          }
          .cl-refresh:hover:not(:disabled) { background: #eff6ff !important; color: #1d4ed8 !important; }
          .cl-default-tag { display: inline-flex; align-items: center; padding: 1px 7px; border-radius: 5px; font-size: 10.5px;
                            font-weight: 600; background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; flex-shrink: 0; }

          .cl-list-row--skeleton {
            pointer-events: none;
            cursor: default;
          }
          @keyframes cl-shimmer {
            0% { opacity: 0.45; }
            50% { opacity: 0.95; }
            100% { opacity: 0.45; }
          }
          .cl-skel {
            display: block;
            border-radius: 6px;
            background: linear-gradient(90deg, #eef2f7 0%, #e2e8f0 50%, #eef2f7 100%);
            background-size: 200% 100%;
            animation: cl-shimmer 1.5s ease-in-out infinite;
          }
          .cl-skel-chevron {
            width: 28px;
            height: 28px;
            border-radius: 8px;
            justify-self: start;
          }
          .cl-skel-name {
            height: 14px;
            width: 72%;
            max-width: 180px;
          }
          .cl-skel-chip {
            height: 24px;
            width: 58px;
            border-radius: 8px;
            justify-self: start;
          }
          .cl-skel-text {
            height: 12px;
            width: 70%;
            max-width: 110px;
            justify-self: start;
          }
          .cl-skel-action {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            justify-self: end;
          }
          @media (max-width: 1100px) {
            .cl-expand-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
            .cl-expand-meta { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
          @media (max-width: 900px) {
            .cl-cloud-head { display: none !important; }
            .cl-cloud-row { grid-template-columns: 36px 1fr !important; gap: 10px !important; }
            .cl-cloud-row > :nth-child(n+3) { grid-column: 2; }
          }
          @media (max-width: 640px) {
            .cl-expand-grid { grid-template-columns: 1fr !important; }
            .cl-expand-meta { grid-template-columns: 1fr !important; }
          }
        `}</style>

        <div style={{ fontFamily: T.fontSans, padding: "20px 24px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "#172033", lineHeight: 1.2 }}>Clouds</div>
              <div style={{ fontSize: 15, color: "#667085", marginTop: 6 }}>Manage cloud providers and connected services</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: `0.5px solid ${T.borderMid}`, borderRadius: 8, padding: "0 12px", height: 34, width: 220 }}>
                <span style={{ color: T.textMuted, display: "flex" }}><Ic.Search /></span>
                <input
                  type="text"
                  placeholder="Search clouds…"
                  value={this.state.searchValue}
                  onChange={this.handleSearchChange}
                  autoComplete="off"
                  style={{ border: "none", background: "transparent", fontFamily: T.fontSans, fontSize: 13, color: T.textPrimary, outline: "none", width: "100%" }}
                />
              </div>
              <SATooltip title="Refresh cloud list">
                <button
                  className="cl-refresh"
                  type="button"
                  onClick={this.refreshCloudList}
                  disabled={this.state.loading}
                  style={{
                    width: 34,
                    height: 34,
                    border: `0.5px solid ${T.borderMid}`,
                    borderRadius: 8,
                    background: T.surface,
                    cursor: this.state.loading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: T.textSecondary,
                    opacity: this.state.loading ? 0.7 : 1,
                  }}
                >
                  <span style={{ display: "inline-flex", animation: this.state.loading ? "spin 0.8s linear infinite" : "none" }}>
                    <Ic.Refresh />
                  </span>
                </button>
              </SATooltip>
              <BtnPrimary onClick={this.showCloudActionForm}>
                <Ic.Plus /> Add cloud
              </BtnPrimary>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(this.state.loading || filtered.length > 0) && (
              <div className="cl-cloud-head">
                <span />
                <SortableHead label="Cloud" sortKey="name" activeKey={this.state.sortKey} sortDir={this.state.sortDir} onSort={this.handleSort} />
                <SortableHead label="Status" sortKey="status" activeKey={this.state.sortKey} sortDir={this.state.sortDir} onSort={this.handleSort} />
                <SortableHead label="Type" sortKey="type" activeKey={this.state.sortKey} sortDir={this.state.sortDir} onSort={this.handleSort} />
                <SortableHead label="Services" sortKey="services" activeKey={this.state.sortKey} sortDir={this.state.sortDir} onSort={this.handleSort} />
                <SortableHead label="Created by" sortKey="createdBy" activeKey={this.state.sortKey} sortDir={this.state.sortDir} onSort={this.handleSort} />
                <SortableHead label="Modified" sortKey="modified" activeKey={this.state.sortKey} sortDir={this.state.sortDir} onSort={this.handleSort} />
                <span style={{ textAlign: "right" }}>Actions</span>
              </div>
            )}

            {this.state.loading ? (
              <CloudListSkeleton rows={5} />
            ) : filtered.length > 0 ? (
              filtered.map((pData, idx) => {
                const cid = pData.id || pData.name || idx;
                const expanded = this.state.expandedId === cid;
                const serviceBadges = Array.isArray(pData.services) ? pData.services : [];
                const statusMeta = getCloudStatusMeta(pData.provisionerStatus === "ENABLED");
                const stop = (e) => e.stopPropagation();

                return (
                  <div key={cid} className={`cl-cloud-card${expanded ? " expanded" : ""}`}>
                    <div
                      className="cl-cloud-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => this.toggleRow(cid)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          this.toggleRow(cid);
                        }
                      }}
                    >
                      <button
                        type="button"
                        className="cl-cloud-expand-btn"
                        aria-expanded={expanded}
                        aria-label={expanded ? "Collapse cloud details" : "Expand cloud details"}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.toggleRow(cid);
                        }}
                      >
                        {expanded ? <Ic.MinusSquare /> : <Ic.PlusSquare />}
                      </button>

                      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <CloudAvatar name={pData.name} provisionerType={pData.provisionerType} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {pData.name}
                            </span>
                            {pData.platformDefault && <span className="cl-default-tag">Default</span>}
                          </div>
                          <div style={{ marginTop: 4, fontSize: 11, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {pData.description?.trim() || pData.provisionerType || "Cloud provider"}
                          </div>
                        </div>
                      </div>

                      <span className="cl-meta-chip" style={{ justifySelf: "start", background: statusMeta.bg, color: statusMeta.color, borderColor: statusMeta.border }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusMeta.dot, flexShrink: 0, marginRight: 2 }} />
                        {statusMeta.label}
                      </span>

                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <img src={provisionerTypeLogo(pData.provisionerType)} alt={pData.provisionerType} style={{ width: 20, height: 20, objectFit: "contain" }} />
                        <span style={{ fontSize: 12, color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {pData.provisionerType || "—"}
                        </span>
                      </div>

                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minWidth: 0 }}>
                        {serviceBadges.length > 0
                          ? serviceBadges.slice(0, 2).map((s, i) => <ServiceBadge key={i} label={s} />)
                          : <span style={{ fontSize: 12, color: T.textMuted }}>—</span>}
                        {serviceBadges.length > 2 && (
                          <span className="cl-meta-chip">+{serviceBadges.length - 2}</span>
                        )}
                      </div>

                      <div style={{ fontSize: 12, color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {pData?.createdBy === "SuperAgentx Admin" ? "SuperAgentx" : (pData.createdBy || "—")}
                      </div>

                      <div style={{ fontSize: 12, color: T.textSecondary }}>
                        {pData.updatedDate
                          ? moment.utc(pData.updatedDate).local().format("MMM DD, YYYY")
                          : "—"}
                      </div>

                      <div className="cl-col-actions" onClick={stop}>
                        {!pData.platformDefault && (
                          <>
                            <SATooltip title="Edit">
                              <button
                                className="cl-act"
                                type="button"
                                disabled
                                style={{ width: 32, height: 32, padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, cursor: "not-allowed", display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.textMuted, opacity: 0.4, boxSizing: "border-box" }}
                              >
                                <LuPencil size={14} style={{ flexShrink: 0 }} />
                              </button>
                            </SATooltip>
                            <SATooltip title="Delete cloud">
                              <button
                                className="cl-act del"
                                type="button"
                                onClick={(e) => this.removeCloudConfirmation(e, pData)}
                                style={{ width: 32, height: 32, padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#dc2626", boxSizing: "border-box" }}
                              >
                                <LuTrash2 size={14} style={{ flexShrink: 0 }} />
                              </button>
                            </SATooltip>
                          </>
                        )}
                      </div>
                    </div>

                    {expanded && <CloudExpandPanel item={pData} />}
                  </div>
                );
              })
            ) : (
              !this.state.loading && (
                <div style={{ textAlign: "center", padding: "56px 24px", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", borderRadius: 14, border: `1px dashed ${T.borderMid}` }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", background: "#eff6ff", color: T.accent }}>
                    <Ic.Cloud />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>
                    {hasFilters ? "No clouds found" : "No clouds yet"}
                  </div>
                  <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 18, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                    {hasFilters ? "Try a different search term." : "Add your first cloud provider to get started."}
                  </p>
                  {!hasFilters && (
                    <BtnPrimary onClick={this.showCloudActionForm}>
                      <Ic.Plus /> Add cloud
                    </BtnPrimary>
                  )}
                </div>
              )
            )}

            {!this.state.loading && totalPages > 1 ? (
              <div className="cl-pagination" style={{ flexShrink: 0, paddingTop: 4 }}>
                <SAPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={this.state.pageSize || 10}
                  onPageChange={this.handlePaginationClick}
                  showGoToPage
                  size="sm"
                  className="cl-pagination-bar"
                />
              </div>
            ) : null}
          </div>
        </div>

        <SAModalConfirm
          show={this.state.deleteFlag}
          title="Delete cloud"
          message={
            <>
              Are you sure you want to delete{" "}
              <strong>{this.state.modelItem?.name}</strong>? This action cannot be undone.
            </>
          }
          handleOk={this.handleSubmitModal}
          handleCancel={() => this.setState({ deleteFlag: false, modelItem: {} })}
        />
      </>
    );
  }
}

export default withNavigate(CloudList);
